import { useState } from "react";
import moment from "moment-timezone";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  CommodityOptions,
  ContractTypeOptions,
  type OtcLayerAmendmentType,
  useGenerateLayersByReport,
  LayersByReportFiltersValidation,
} from "@/lib/api/otc";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  AMENDMENT_TYPE_OPTIONS,
  type AmendmentTypeOption,
} from "../contract-form-v3/amendmentTypes";
import { BearDatePicker, BearSelectInput } from "../ContractForm";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

interface LayersByReportFilters {
  after_date?: Date;
  before_date?: Date;
  is_net_new: boolean;
  amendment_type?: OtcLayerAmendmentType;
  commodity?: string;
  contract_type?: string;
}

const amendmentTypeOptions = AMENDMENT_TYPE_OPTIONS.map(
  (option: AmendmentTypeOption): { value: string; label: string } => ({
    value: option.amendment_type,
    label: option.label,
  }),
);

export function LayersByReportCard() {
  const { toast } = useToast();
  const generateReport = useGenerateLayersByReport();
  const [filters, setFilters] = useState<LayersByReportFilters>({ is_net_new: false });
  const [link, setLink] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDisabled = !filters.after_date || !filters.before_date;

  const onGenerate = async () => {
    if (isDownloading || isDisabled) return;
    setLink(undefined);
    setIsDownloading(true);

    try {
      const parsed = LayersByReportFiltersValidation.safeParse({
        after_date: filters.after_date
          ? moment.tz(filters.after_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
        before_date: filters.before_date
          ? moment.tz(filters.before_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
        ...(filters.is_net_new ? { is_net_new: true } : {}),
        ...(filters.amendment_type ? { amendment_type: filters.amendment_type } : {}),
        ...(filters.commodity ? { commodity: filters.commodity } : {}),
        ...(filters.contract_type ? { contract_type: filters.contract_type } : {}),
      });

      if (!parsed.success) {
        toast({ title: "Invalid Filters", description: "" });
        return;
      }

      const result = await generateReport(parsed.data);
      if (!result.success) {
        throw result;
      }
      setLink(result.data.link);
    } catch (error: unknown) {
      console.error("Failed to generate otc layers by report", error);
      toast({ title: "Failed to generate", description: "" });
    } finally {
      setIsDownloading(false);
    }
  };

    const commonComboBoxOverrides = {
        dwimOnChange: true,
        popoverClassName: 'w-[400px] h-[300px]',
        virtualizeList: true,
    };

  return (
    <ReportCard
      title="Layers By Report"
      description="Shows all contracts layers for when there is a matching layer"
      isDownloading={isDownloading}
      isGenerateDisabled={isDisabled}
      onGenerate={onGenerate}
      hasResults={!!link}
      linkContent={link ? <ReportDownloadLink href={link} label="Download Report" /> : null}
    >
      <div className="grid grid-cols-1 gap-3">
        <BearDatePicker
          tabIndex={0}
          name="layers_by_report_after_date"
          label="After Date"
          value={filters.after_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((previous: LayersByReportFilters) => ({ ...previous, after_date: date }))
          }
        />
        <BearDatePicker
          tabIndex={1}
          name="layers_by_report_before_date"
          label="Before Date"
          value={filters.before_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((previous: LayersByReportFilters) => ({ ...previous, before_date: date }))
          }
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="layers_by_report_is_net_new"
            checked={filters.is_net_new}
            onCheckedChange={(checked: boolean | "indeterminate") =>
              setFilters((previous: LayersByReportFilters) => ({
                ...previous,
                is_net_new: checked === true,
              }))
            }
          />
          <Label htmlFor="layers_by_report_is_net_new" className="font-semibold">
            Net New Contracts Only
          </Label>
        </div>
        <BearSelectInput
          tabIndex={2}
          name="layers_by_report_amendment_type"
          label="Amendment Type"
          value={filters.amendment_type ?? ""}
          onValueChange={(value: string) =>
            setFilters((previous: LayersByReportFilters) => ({
              ...previous,
              amendment_type: value
                ? value as OtcLayerAmendmentType
                : undefined,
            }))
          }
          options={amendmentTypeOptions}
          comboBoxPropsOverrides={commonComboBoxOverrides}
        />
        <BearSelectInput
          tabIndex={3}
          name="layers_by_report_commodity"
          label="Commodity"
          value={filters.commodity ?? ""}
          onValueChange={(value: string) =>
            setFilters((previous: LayersByReportFilters) => ({
              ...previous,
              commodity: value || undefined,
            }))
          }
          options={CommodityOptions}
          comboBoxPropsOverrides={commonComboBoxOverrides}
        />
        <BearSelectInput
          comboBoxPropsOverrides={commonComboBoxOverrides}
          tabIndex={4}
          name="layers_by_report_contract_type"
          label="Contract Type"
          value={filters.contract_type ?? ""}
          onValueChange={(value: string) =>
            setFilters((previous: LayersByReportFilters) => ({
              ...previous,
              contract_type: value || undefined,
            }))
          }
          options={ContractTypeOptions}
        />
      </div>
    </ReportCard>
  );
}

